import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Users, MessageSquare, User } from 'lucide-react';
import type { FormTemplate } from '../types';
import { getAllTemplates, getTemplatesByCategory } from '../utils/formTemplates';
import { Modal } from './Modal';

interface TemplateLoaderProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadTemplate: (template: FormTemplate) => void;
}

const TemplateLoader: React.FC<TemplateLoaderProps> = ({
  isOpen,
  onClose,
  onLoadTemplate
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);

  const allTemplates = getAllTemplates();
  const categories = [
    { id: 'all', label: 'All Templates', icon: FileText },
    { id: 'registration', label: 'Registration Forms', icon: Users },
    { id: 'feedback', label: 'Feedback Forms', icon: MessageSquare },
    { id: 'custom', label: 'Basic Templates', icon: User }
  ];

  const filteredTemplates = selectedCategory === 'all' 
    ? allTemplates 
    : getTemplatesByCategory(selectedCategory as FormTemplate['category']);

  const getCategoryIcon = (category: FormTemplate['category']) => {
    switch (category) {
      case 'registration': return Users;
      case 'feedback': return MessageSquare;
      case 'custom': return User;
      default: return FileText;
    }
  };

  const handleLoadTemplate = (template: FormTemplate) => {
    onLoadTemplate(template);
    onClose();
  };

  const toggleExpanded = (templateId: string) => {
    setExpandedTemplate(expandedTemplate === templateId ? null : templateId);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Load Form Template">
      <div className="max-w-4xl mx-auto">
        {/* Category Filter */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Categories</h3>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                    selectedCategory === category.id
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {category.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Templates List */}
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No templates found</h3>
              <p className="mt-2 text-gray-600">
                No templates available in the selected category.
              </p>
            </div>
          ) : (
            filteredTemplates.map((template) => {
              const Icon = getCategoryIcon(template.category);
              const isExpanded = expandedTemplate === template.id;
              
              return (
                <div
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <Icon className="h-5 w-5 text-gray-400 mr-3" />
                      <div className="flex-1">
                        <h4 className="text-lg font-medium text-gray-900">{template.name}</h4>
                        <p className="text-sm text-gray-600">{template.description}</p>
                        <div className="flex items-center mt-2 space-x-4">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                            {template.category}
                          </span>
                          <span className="text-xs text-gray-500">
                            {template.fields.length} fields
                          </span>
                          {template.isSystem && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              System Template
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => toggleExpanded(template.id)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="h-4 w-4 mr-1" />
                            Hide Fields
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4 mr-1" />
                            Preview Fields
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleLoadTemplate(template)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Load Template
                      </button>
                    </div>
                  </div>

                  {/* Field Preview */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Fields in this template:</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {template.fields.map((field) => (
                          <div
                            key={field.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                          >
                            <div className="flex-1">
                              <div className="flex items-center">
                                <span className="text-sm font-medium text-gray-900">
                                  {field.label}
                                </span>
                                {field.required && (
                                  <span className="ml-1 text-red-500 text-sm">*</span>
                                )}
                              </div>
                              <div className="flex items-center mt-1 space-x-2">
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-800 capitalize">
                                  {field.type}
                                </span>
                                {field.options && field.options.length > 0 && (
                                  <span className="text-xs text-gray-500">
                                    {field.options.length} options
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TemplateLoader;
